import React, { useState } from 'react';
import { X, CheckSquare, Square, Gift } from 'lucide-react';

const BulkAddAmenitiesModal = ({ event, onClose, onDone }) => {
    const [rawIds, setRawIds] = useState('');
    const [selectedAmenities, setSelectedAmenities] = useState(new Set());
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');

    // Collect all unique amenities: event-level + category-level
    const allAmenities = Array.from(new Set([
        ...(event?.amenities || []),
        ...(event?.participantCategories || []).flatMap(c => c.amenities || [])
    ]));

    const toggleAmenity = (name) => {
        setSelectedAmenities(prev => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const selectAll = () => setSelectedAmenities(new Set(allAmenities));
    const deselectAll = () => setSelectedAmenities(new Set());

    const parseIds = (raw) =>
        raw.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);

    const handleSubmit = async () => {
        setError('');
        setResult(null);
        const participantIds = parseIds(rawIds);
        if (participantIds.length === 0) {
            setError('Please paste at least one participant ID.');
            return;
        }
        if (selectedAmenities.size === 0) {
            setError('Please select at least one amenity.');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/v2/events/${event._id}/participants/bulk-add-amenities`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ participantIds, amenities: [...selectedAmenities] })
                }
            );
            const data = await res.json();
            if (data.success) {
                setResult(data);
                if (onDone) onDone();
            } else {
                setError(data.message || 'Failed to add amenities.');
            }
        } catch (err) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center gap-2">
                        <Gift className="w-5 h-5 text-teal-600" />
                        <h2 className="text-lg font-semibold text-gray-900">Add Amenities to Participants</h2>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                    {/* Participant IDs */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Participant IDs
                            <span className="ml-1 text-gray-400 font-normal">(one per line or comma-separated)</span>
                        </label>
                        <textarea
                            rows={5}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
                            placeholder={"683abc123def456...\n683xyz789ghi012..."}
                            value={rawIds}
                            onChange={e => setRawIds(e.target.value)}
                        />
                        {rawIds.trim() && (
                            <p className="mt-1 text-xs text-gray-500">
                                {parseIds(rawIds).length} ID(s) detected
                            </p>
                        )}
                    </div>

                    {/* Amenity Selection */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-gray-700">
                                Select Amenities
                            </label>
                            <div className="flex gap-2 text-xs">
                                <button onClick={selectAll} className="text-teal-600 hover:underline">All</button>
                                <button onClick={deselectAll} className="text-gray-500 hover:underline">None</button>
                            </div>
                        </div>
                        {allAmenities.length === 0 ? (
                            <p className="text-sm text-gray-500 italic">
                                No amenities configured for this event. Add amenities in event settings first.
                            </p>
                        ) : (
                            <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                                {allAmenities.map(name => (
                                    <button
                                        key={name}
                                        onClick={() => toggleAmenity(name)}
                                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-gray-50 transition-colors"
                                    >
                                        {selectedAmenities.has(name)
                                            ? <CheckSquare className="w-4 h-4 text-teal-600 flex-shrink-0" />
                                            : <Square className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                        }
                                        <span className={selectedAmenities.has(name) ? 'font-medium text-gray-900' : 'text-gray-700'}>
                                            {name}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                            {error}
                        </div>
                    )}

                    {/* Result */}
                    {result && (
                        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm text-green-800 space-y-1">
                            <p className="font-medium">{result.message}</p>
                            {result.skippedCount > 0 && (
                                <p className="text-green-700">
                                    {result.skippedCount} participant(s) already had all selected amenities — skipped.
                                </p>
                            )}
                            {result.notFoundIds?.length > 0 && (
                                <p className="text-orange-700">
                                    {result.notFoundIds.length} ID(s) not found in this event.
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                        {result ? 'Close' : 'Cancel'}
                    </button>
                    {!result && (
                        <button
                            onClick={handleSubmit}
                            disabled={loading || allAmenities.length === 0}
                            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Adding...' : 'Add Amenities'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkAddAmenitiesModal;
